/**
 * useSpecialShowcases Hook
 * Fetches special showcase types: recently_viewed, trending, new_arrivals, best_deals, your_wishlist, recent_searches, holiday
 * Single fetch on mount, no pagination
 */

import { useState, useEffect, useCallback } from 'react';
import { appendShuffleSeed } from '../utils/seeded-shuffle';
import { parseJsonResponse } from '../utils/read-json-response';

interface SpecialShowcase {
  type: 'recently_viewed' | 'trending' | 'new_arrivals' | 'best_deals' | 'your_wishlist' | 'recent_searches' | 'holiday' | 'similar_to_liked' | 'similar_to_purchased' | 'near_you';
  title: string;
  listings: Record<string, unknown>[];
  sourceListingId?: number;
}

interface SpecialShowcasesPage {
  success?: boolean;
  error?: string;
  showcases?: SpecialShowcase[];
}

interface UseSpecialShowcasesOptions {
  type: 'user' | 'session';
  userId?: number;
  sessionId?: string;
  anonymousId?: string;
  days?: number;
  showcaseTypes?: string;
  limit?: number;
  enabled?: boolean;
}

interface UseSpecialShowcasesReturn {
  showcases: SpecialShowcase[];
  isLoading: boolean;
  /** True once the fetch has settled either way. See useTagShowcases. */
  hasLoaded: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getShowcase: (type: SpecialShowcase['type']) => SpecialShowcase | undefined;
  getShowcases: (type: SpecialShowcase['type']) => SpecialShowcase[];
}

export function useSpecialShowcases(options: UseSpecialShowcasesOptions): UseSpecialShowcasesReturn {
  const [showcases, setShowcases] = useState<SpecialShowcase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchShowcases = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        type: options.type,
        ...(options.userId && { userId: String(options.userId) }),
        ...(options.sessionId && { sessionId: options.sessionId }),
        ...(options.anonymousId && { anonymousId: options.anonymousId }),
        ...(options.days && { days: String(options.days) }),
        ...(options.showcaseTypes && { showcaseTypes: options.showcaseTypes }),
        ...(options.limit && { limit: String(options.limit) }),
      });

      const response = await fetch(`/api/recommendations/special-showcases?${appendShuffleSeed(params)}`);
      // An empty body read with response.json() is a bare "Unexpected end of
      // JSON input" with no status and no route (cluster b9ec50e1f8037199).
      const data = await parseJsonResponse<SpecialShowcasesPage>(
        response,
        'GET /api/recommendations/special-showcases'
      );

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch special showcases');
      }

      // A success always carries the array (SpecialShowcasesSuccessResponseSchema).
      setShowcases(data.showcases!);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('[useSpecialShowcases] Error:', err);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [options.type, options.userId, options.sessionId, options.anonymousId, options.days, options.showcaseTypes, options.limit]);

  const getShowcase = useCallback((type: SpecialShowcase['type']) => {
    return showcases.find(s => s.type === type);
  }, [showcases]);

  const getShowcases = useCallback((type: SpecialShowcase['type']) => {
    return showcases.filter(s => s.type === type);
  }, [showcases]);

  useEffect(() => {
    if (options.enabled !== false) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- fetches the special showcases when the hook's inputs change: the setState calls are the request's own loading/error/result flags inside the async fetchShowcases; the rule follows the call but cannot see the await boundary
      fetchShowcases();
    }
  }, [
    options.type,
    options.userId,
    options.sessionId,
    options.anonymousId,
    options.days,
    options.showcaseTypes,
    options.limit,
    options.enabled,
  ]);

  return {
    showcases,
    isLoading,
    hasLoaded,
    error,
    refetch: fetchShowcases,
    getShowcase,
    getShowcases,
  };
}
